import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiCalendar, FiUser, FiArrowLeft, FiClock, FiShare2 } from 'react-icons/fi';
import SEO from '../components/SEO';

const BlogDetails = () => {
    const { id } = useParams();
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language ? i18n.language.startsWith('ar') : false;

    const [post, setPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const fallbackPosts = [
        {
            id: 1,
            title: t('blogs.fallback.post1.title'),
            content: t('blogs.fallback.post1.content'),
            date: 'Oct 15, 2025',
            author: t('blogs.author'),
            image: '/images/site/blog-commercial.png',
            category: t('blogs.fallback.post1.category'),
            readTime: '5'
        },
        {
            id: 2,
            title: t('blogs.fallback.post2.title'),
            content: t('blogs.fallback.post2.content'),
            date: 'Oct 02, 2025',
            author: t('blogs.author'),
            image: '/images/site/blog-hiddencosts.png',
            category: t('blogs.fallback.post2.category'),
            readTime: '4'
        },
        {
            id: 3,
            title: t('blogs.fallback.post3.title'),
            content: t('blogs.fallback.post3.content'),
            date: 'Sep 28, 2025',
            author: t('blogs.author'),
            image: '/images/site/blog-ropeaccess.png',
            category: t('blogs.fallback.post3.category'),
            readTime: '4'
        }
    ];

    useEffect(() => {
        const fetchPost = async () => {
            setIsLoading(true);
            try {
                const response = await fetch('/api/blogs/read.php');
                if (response.ok) {
                    const data = await response.json();
                    const found = data.records?.find(p => p.id === parseInt(id) || p.id === id);
                    if (found) {
                        setPost(found);
                    } else {
                        setPost(fallbackPosts.find(p => p.id === parseInt(id)) || fallbackPosts[0]);
                    }
                } else {
                    setPost(fallbackPosts.find(p => p.id === parseInt(id)) || fallbackPosts[0]);
                }
            } catch (error) {
                setPost(fallbackPosts.find(p => p.id === parseInt(id)) || fallbackPosts[0]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPost();
        window.scrollTo(0, 0);
    }, [id]);

    if (isLoading) {
        return <div className="min-h-screen pt-32 text-center text-gray-500">{isArabic ? 'جاري التحميل...' : 'Loading...'}</div>;
    }

    if (!post) {
        return (
            <div className="min-h-screen pt-32 text-center container-custom">
                <h2 className="text-2xl font-bold mb-4">{isArabic ? 'المقال غير موجود' : 'Blog Post Not Found'}</h2>
                <Link to="/blogs" className="text-primary-600 hover:underline">{t('blogs.backToBlogs')}</Link>
            </div>
        );
    }

    // Determine localized values
    const isFallback = post.id === 1 || post.id === 2 || post.id === 3 || post.id === '1' || post.id === '2' || post.id === '3';
    const activeTitle = isArabic
        ? (isFallback ? t(`blogs.fallback.post${post.id}.title`) : (post.title_ar || post.title))
        : post.title;

    const activeContent = isArabic
        ? (isFallback ? t(`blogs.fallback.post${post.id}.content`) : (post.content_ar || post.content))
        : post.content;

    const activeCategory = isArabic
        ? (isFallback ? t(`blogs.fallback.post${post.id}.category`) : (post.category_ar || post.category))
        : post.category;

    const rawReadTime = String(post.readTime || '5').replace(/[^0-9]/g, '');
    const displayReadTime = `${rawReadTime} ${t('blogs.readTimeSuffix')}`;
    const displayAuthor = isArabic ? t('blogs.author') : (post.author || 'PrimeForce Team');

    return (
        <div className="bg-gray-50 min-h-screen">
            <SEO
                title={`${activeTitle} | PrimeForce Services`}
                description={activeTitle}
            />

            {/* Header Banner */}
            <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-r from-gray-950 via-primary-950 to-gray-950 text-white overflow-hidden">
                <div className="container-custom relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <span className="inline-block px-4 py-1.5 bg-accent-500 text-white rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider mb-6 shadow-md">
                            {activeCategory}
                        </span>
                        <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6 max-w-4xl mx-auto leading-tight">
                            {activeTitle}
                        </h1>
                        <div className="flex items-center justify-center gap-6 text-sm md:text-base text-gray-300 flex-wrap">
                            <div className="flex items-center gap-2">
                                <FiCalendar className="text-accent-400" /> {post.date}
                            </div>
                            <div className="flex items-center gap-2">
                                <FiUser className="text-accent-400" /> {displayAuthor}
                            </div>
                            <div className="flex items-center gap-2">
                                <FiClock className="text-accent-400" /> {displayReadTime}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Content & Featured Image */}
            <div className="container-custom py-12 md:py-16">
                <div className="max-w-4xl mx-auto">
                    <Link to="/blogs" className="inline-flex items-center gap-2 text-gray-600 hover:text-primary-600 mb-8 font-semibold transition-colors">
                        <FiArrowLeft className="rtl:rotate-180 flex-shrink-0" /> {t('blogs.backToBlogs')}
                    </Link>

                    {/* Featured Image */}
                    {post.image && (
                        <div className="mb-10 rounded-2xl overflow-hidden shadow-xl border border-gray-200">
                            <img
                                src={post.image}
                                alt={activeTitle}
                                className="w-full h-auto max-h-[480px] object-cover"
                            />
                        </div>
                    )}

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="bg-white rounded-2xl p-6 sm:p-10 md:p-14 shadow-sm border border-gray-100"
                    >
                        <div
                            className="prose prose-lg max-w-none text-gray-700 leading-relaxed font-sans"
                            dangerouslySetInnerHTML={{ __html: activeContent }}
                        ></div>

                        {/* Share Section */}
                        <div className="mt-12 pt-8 border-t border-gray-100 flex items-center justify-between">
                            <span className="font-semibold text-gray-800">{t('blogs.share')}</span>
                            <div className="flex gap-4">
                                <button className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-primary-50 hover:text-primary-600 transition-colors">
                                    <FiShare2 />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default BlogDetails;
